/**
 * @file api-product-stock.ts
 * @description Разбивка остатков товара для карточки каталога
 * (phase19.14, inventory.md W9) — `GET /admin/catalog/products/:id/stock`.
 *
 * Отдельный модуль от `api-catalog.ts`: это складской вопрос, заданный из
 * каталога, и на сервере он живёт в `ProductStockService`. Склады здесь НЕ
 * отфильтрованы по `isSellable` — менеджер обязан видеть и карантин, он
 * помечен флагом, а не спрятан.
 */

import { apiFetch } from "./api-client";
import type { WarehouseKind } from "./wms-warehouse-kind";

/** Партия внутри остатка на складе. */
export interface StockLotBreakdownDto {
  lotId: string | null;
  batchNumber: string | null;
  expiresAt: string | null;
  quantity: string;
  reserved: string;
  /** Адрес ячейки хранения партии (W14); null — размещение не задано. */
  binCode: string | null;
}

/** Остаток одной фасовки на конкретном складе. */
export interface SkuWarehouseStockDto {
  warehouse: {
    id: string;
    name: string;
    code: string;
    kind: WarehouseKind;
    isSellable: boolean;
  };
  quantity: string;
  reserved: string;
  available: string;
  lots: StockLotBreakdownDto[];
}

/** Остатки товара: фасовка × склад × партия. */
export interface ProductStockDto {
  productId: string;
  /** false — складской учёт не ведётся (услуга, цифровой товар). */
  stockTracked: boolean;
  /** Доступно суммарно по sellable-складам; null — учёт не ведётся. */
  available: string | null;
  skus: {
    skuId: string;
    code: string;
    displayName: string | null;
    available: string;
    warehouses: SkuWarehouseStockDto[];
  }[];
}

export const productStockApi = {
  get: (productId: string) =>
    apiFetch<ProductStockDto>(`/admin/catalog/products/${productId}/stock`),
};

/**
 * Подпись фасовки в секции «Остатки»: «50 г · IVC-050».
 * Имени варианта может не быть — тогда показываем только артикул.
 */
export function formatStockSkuLabel(sku: {
  code: string;
  displayName: string | null;
}): string {
  const name = sku.displayName?.trim();
  return name ? `${name} · ${sku.code}` : sku.code;
}
